import type { FilterableCarItem } from '@/scopes/checkout/filterableCars/domain/types'
import { Benefits } from '@sunwebgroup/uds-ui/benefits'
import { Button } from '@sunwebgroup/uds-ui/button'
import { RichText } from '@sunwebgroup/uds-ui/rich-text'
import { Sheet } from '@sunwebgroup/uds-ui/sheet'
import { Typography, TypographyProps } from '@sunwebgroup/uds-ui/typography'

import cn from 'classnames'
import { sub } from 'date-fns'
import * as React from 'react'
import { toSnakeCase } from '@/helpers/string-helpers'
import { useIntl } from '@/intl'
import { AdditionalCostBanners } from '@/scopes/checkout/components/services/CarRentalFilteringService/AdditionalCostBanners'
import { CreditCardDriverForm } from '@/scopes/checkout/components/services/CarRentalFilteringService/Driver/CreditCardDriverForm'
import { DriverSheet } from '@/scopes/checkout/components/services/CarRentalFilteringService/Driver/DriverSheet'
import { EquipmentsSheet } from '@/scopes/checkout/components/services/CarRentalFilteringService/Equipments/EquipmentsSheet'
import {
  carIsBeingUpgraded,
  carSelectionLoading,
  carToBeUpgraded,
  editingCar,
  useCarRentalFilteringServiceStore,
} from '@/scopes/checkout/components/services/CarRentalFilteringService/store'
import { removeCar, upgradeCar } from '@/scopes/checkout/domain/actions/services/carRentalFiltering'
import { createCarEvent } from '@/scopes/checkout/domain/analytics'
import { useBookingDraftQuery, useService } from '@/scopes/checkout/domain/hooks'
import { type CheckoutCarRentalServiceOption, ServiceType } from '@/scopes/checkout/domain/types'
import { buildUniqOptionCarId } from '@/scopes/checkout/infrastructure/api/service-mappers/carRentalFilteringServiceMapper'
import { MediaServerLazyImage } from '@/scopes/common/components/MediaServerLazyImage'
import { useBreakpoints } from '@/scopes/common/components/utils/breakpoints'
import { parseDateForLocalFormatting } from '@/scopes/common/domain/utils/dates'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import styles from './CarOption.module.css'

interface CarItemProps extends React.HTMLAttributes<HTMLDivElement> {
  isSelected?: boolean
}

export function CarItem({ isSelected = false, className, children, ...props }: CarItemProps) {
  return (
    <div {...props} className={cn(styles.carItem, isSelected && styles.carItemSelected, className)}>
      {children}
    </div>
  )
}

export function CarRentalCard({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div {...props} className={cn(styles.carRentalCard, className)}>
      {children}
    </div>
  )
}

export function CarCardFooterSection({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div {...props} className={cn(styles.carCardFooterSection, className)}>
      {children}
    </div>
  )
}

export function CarCardFooter({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div {...props} className={cn(styles.carCardFooter, className)}>
      {children}
    </div>
  )
}

export function Price({ className, children, ...props }: TypographyProps) {
  return (
    <Typography {...props} className={cn(styles.price, className)} variant="body-m-bold-tight">
      {children}
    </Typography>
  )
}

export function PriceDetail({ className, children, ...props }: TypographyProps) {
  return (
    <Typography {...props} className={cn(styles.priceDetail, className)} variant="body-s-regular-flat">
      {children}
    </Typography>
  )
}

export function ActionButtons({ className, children }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn(styles.actionButtonWrapper, className)}>
      {children}
    </div>
  )
}

function CarOptionForms({ openDriverByDefault = false, ...option }: CheckoutCarRentalServiceOption & { openDriverByDefault?: boolean }) {
  return (
    <>
      <DriverSheet {...option} openByDefault={openDriverByDefault} />
      <EquipmentsSheet {...option} />
      {option.isIncluded && <CreditCardDriverForm {...option} />}
    </>
  )
}

export function CarOption({ isSelected, openDriverByDefault = false, ...option }: CheckoutCarRentalServiceOption & { isSelected: boolean, openDriverByDefault?: boolean }) {
  const { service } = useService(ServiceType.CarRentalFiltering)

  const intl = useIntl()

  if (!service)
    return null

  const { rooms } = service
  if (!rooms)
    return null

  const roomName = Object.values(rooms)
    .find(room => room.selectedValue === option.id)
    ?.name || ''

  return (
    <CarItem isSelected={isSelected}>
      {isSelected && !option.isIncluded && (
        <Typography className={styles.yourSelectionBadge} variant="body-s-bold-tight">
          {intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_your_selection' })}
        </Typography>
      )}
      {isSelected && option.isIncluded && (
        <Typography className={styles.yourSelectionBadge} variant="body-s-bold-tight">
          {intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_is_included_with_room' }, { roomName })}
        </Typography>
      )}
      <div className={cn(styles.carItemContent, isSelected && styles.carItemContentSelected)}>
        <CarOptionCardView isSelected={isSelected} {...option} />
        {isSelected && <CarOptionForms {...option} openDriverByDefault={openDriverByDefault} />}
      </div>
    </CarItem>
  )
}

export function CarOptionCardView({ isSelected, ...option }: CheckoutCarRentalServiceOption & { isSelected: boolean }) {
  const { id: carId, price, driverId: currentDriverIdSelected, canBeRemoved, isIncluded, canBeUpgraded, equipments, selectedEquipments, value: optionValue } = option
  const { sendEvent } = useAnalytics()
  const { isTablet } = useBreakpoints()
  const { service, setServiceValue } = useService(ServiceType.CarRentalFiltering)
  const { configurations } = service || {}
  const driver = currentDriverIdSelected ? service?.participants[currentDriverIdSelected] : null

  const defaultDriverBirthdate = sub(new Date(), { years: configurations?.rentalConditionsMinAgeDriver })

  const { startEditingCar, endEditingCar, setCarIsBeingUpgraded, setCarToBeUpgraded, setShowExtraCars, setCarToBeSelected } = useCarRentalFilteringServiceStore()
  const { isFetching: isGettingBookingDraft } = useBookingDraftQuery()
  const isCarBeingSelected = useCarRentalFilteringServiceStore(carSelectionLoading)
  const isCarBeingUpgraded = useCarRentalFilteringServiceStore(carIsBeingUpgraded)
  const carValueToBeUpgraded = useCarRentalFilteringServiceStore(carToBeUpgraded)

  const disableButtons = isCarBeingSelected || isGettingBookingDraft

  const intl = useIntl()

  const optionId = buildUniqOptionCarId(carId, optionValue)
  const currentEditingCar = useCarRentalFilteringServiceStore(editingCar(carValueToBeUpgraded))
  const shouldShowPrice = !isIncluded || (isIncluded && price > 0)
  const handleSelectClick = React.useCallback((carValue: string) => {
    if (!carValue)
      return
    if (!service)
      return

    if (isCarBeingUpgraded) {
      if (currentEditingCar) {
        const {
          driverId: currentSelectedDriverId,
        } = currentEditingCar

        setServiceValue(upgradeCar({ value: carValue, participantId: currentSelectedDriverId ?? '', id: carId })(service))
      }

      setCarIsBeingUpgraded(false)
      setCarToBeUpgraded('')
      endEditingCar(optionId)

      sendEvent(
        createCarEvent({
          result: 'upgrade',
          type: carValue,
          description: 'upgrade button clicked',
          element: 'button',
        }),
      )
    }

    setCarToBeSelected(optionId)
    setShowExtraCars(false)

    if (!currentDriverIdSelected && !currentEditingCar) {
      startEditingCar(optionId, {
        driverId: null,
        driverBirthDate: defaultDriverBirthdate,
        driverHasCreditCard: false,
        bookedEquipmentsWithQuantity: [],
        isEditing: false,
      })
    }
  }, [currentEditingCar, endEditingCar, isCarBeingUpgraded, setCarToBeUpgraded, setShowExtraCars, defaultDriverBirthdate, currentDriverIdSelected, sendEvent, setCarToBeSelected, setCarIsBeingUpgraded, setServiceValue, startEditingCar, carId, service, optionId])

  const handleRemoveClick = React.useCallback((carValue: string) => {
    if (!carValue)
      return
    if (!service)
      return
    if (!currentDriverIdSelected) {
      setCarToBeSelected('')
    }
    if (currentDriverIdSelected) {
      setServiceValue(removeCar({ participantId: currentDriverIdSelected ?? '' })(service))
      sendEvent(
        createCarEvent({
          result: 'remove',
          type: carValue,
          description: 'remove button clicked',
          element: 'button',
        }),
      )
    }
  }, [sendEvent, currentDriverIdSelected, setServiceValue, setCarToBeSelected, service])

  const handleChangeClick = React.useCallback((carValue: string) => {
    if (!carValue)
      return
    if (!service)
      return
    setCarIsBeingUpgraded(true)
    setCarToBeUpgraded(optionId)

    if (!driver)
      return
    startEditingCar(
      optionId,
      {
        driverId: currentDriverIdSelected,
        driverBirthDate: driver.birthdate ? parseDateForLocalFormatting(driver.birthdate) : new Date(defaultDriverBirthdate),
        driverHasCreditCard: true,
        bookedEquipmentsWithQuantity: equipments.filter(({ id }) => selectedEquipments?.includes(id)).map(({ id, quantitySelected }) => {
          return {
            id,
            quantity: quantitySelected ?? 1,
          }
        }),
        isEditing: true,
      },
    )

    sendEvent(
      createCarEvent({
        result: 'upgrade',
        type: carValue,
        description: 'upgrade button clicked',
        element: 'button',
      }),
    )
  }, [optionId, driver, currentDriverIdSelected, equipments, selectedEquipments, setCarToBeUpgraded, startEditingCar, sendEvent, setCarIsBeingUpgraded, service, defaultDriverBirthdate])

  if (!service)
    return

  return (
    <CarRentalCard>
      <CarCardBody {...option} />
      <CarCardFooterSection>
        <CarCardFooter>
          <Price>
            { !shouldShowPrice && intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_included_label' }) }
            { shouldShowPrice && intl.formatTruncatedCurrency(price, { showSign: isIncluded })}
          </Price>
          { isIncluded && service.carProviderIsOutOfService && (
            <PriceDetail>
              {intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_included_car_upgraded_after_booking_text' })}
            </PriceDetail>
          )}
          <ActionButtons>
            {!isSelected && (
              <Button
                variant="secondary"
                onClick={() => handleSelectClick(carId)}
                size="S"
                disabled={disableButtons}
                className={styles.actionButton}
              >
                {intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_select_car_text' })}
              </Button>
            )}
            {isSelected && (
              <>
                {canBeRemoved && !isIncluded && (
                  <Button
                    variant="secondary"
                    onClick={() => handleRemoveClick(carId)}
                    size="S"
                    disabled={disableButtons}
                    className={styles.actionButton}
                  >
                    {isTablet && <Button.Icon size={16} glyph="Dismiss" /> }
                    {intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_remove_car_text' })}
                  </Button>
                )}
                {canBeUpgraded && (
                  <Button
                    variant="secondary"
                    onClick={() => handleChangeClick(carId)}
                    size="S"
                    disabled={disableButtons}
                    className={styles.actionButton}
                  >
                    { isTablet && isIncluded && (<Button.Icon size={16} glyph="ArrowUpFromLine" />) }
                    { isTablet && !isIncluded && (<Button.Icon size={16} glyph="Pencil" />) }
                    { isIncluded ? intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_upgrade_car_text' }) : intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_change_car_text' })}
                  </Button>
                )}
              </>
            )}

          </ActionButtons>
        </CarCardFooter>
      </CarCardFooterSection>
    </CarRentalCard>
  )
}

type FeatureType = 'manual' | 'automatic' | 'airconditioning' | 'unknown' | 'small' | 'economy' | 'compact' | 'intermediate' | 'fullSize' | 'convertible' | 'mini_van' | 'suv' | 'compact_station' | 'none' | 'credit_card' | 'banktransfer' | 'electronicpayment' | 'debit_card' | 'debit_cash' | 'desk_at_airport' | 'shuttle_service' | 'representant_service' | '4_wheel_drive'

function featureTextKeys(key: FeatureType): string | undefined {
  const mapping: Record<string, string> = {
    'manual': 'checkout_steps_ancillaries_car_rental_filtering_feature_manual_text',
    'automatic': 'checkout_steps_ancillaries_car_rental_filtering_feature_automatic_text',
    'airconditioning': 'checkout_steps_ancillaries_car_rental_filtering_feature_airconditioning_text',
    'unknown': 'checkout_steps_ancillaries_car_rental_filtering_feature_unknown_text',
    'small': 'checkout_steps_ancillaries_car_rental_filtering_feature_small_text',
    'economy': 'checkout_steps_ancillaries_car_rental_filtering_feature_economy_text',
    'compact': 'checkout_steps_ancillaries_car_rental_filtering_feature_compact_text',
    'intermediate': 'checkout_steps_ancillaries_car_rental_filtering_feature_intermediate_text',
    'fullSize': 'checkout_steps_ancillaries_car_rental_filtering_feature_fullSize_text',
    'convertible': 'checkout_steps_ancillaries_car_rental_filtering_feature_convertible_text',
    'mini_van': 'checkout_steps_ancillaries_car_rental_filtering_feature_mini_van_text',
    'suv': 'checkout_steps_ancillaries_car_rental_filtering_feature_suv_text',
    'compact_station': 'checkout_steps_ancillaries_car_rental_filtering_feature_compact_station_text',
    'none': 'checkout_steps_ancillaries_car_rental_filtering_feature_none_deposit_payment_text',
    'credit_card': 'checkout_steps_ancillaries_car_rental_filtering_feature_credit_card_text',
    'banktransfer': 'checkout_steps_ancillaries_car_rental_filtering_feature_banktransfer_text',
    'debit_card': 'checkout_steps_ancillaries_car_rental_filtering_feature_debit_card_text',
    'electronicpayment': 'checkout_steps_ancillaries_car_rental_filtering_feature_debit_card_text', // This is an assumption as the electronic payment method can be similar to debit card, if this is not the case we need a new key for this feature
    'debit_cash': 'checkout_steps_ancillaries_car_rental_filtering_feature_debit_cash_text',
    'desk_at_airport': 'checkout_steps_ancillaries_car_rental_filtering_feature_desk_at_airport_text',
    'shuttle_service': 'checkout_steps_ancillaries_car_rental_filtering_feature_shuttle_service_text',
    'representant_service': 'checkout_steps_ancillaries_car_rental_filtering_feature_representant_service_text',
    '4_wheel_drive': 'checkout_steps_ancillaries_car_rental_filtering_feature_4_wheel_drive_text',
  }
  return mapping[key]
}

export function CarCardBody({ ...option }: CheckoutCarRentalServiceOption | FilterableCarItem) {
  const { id, label, features, extraFeatures = [], conditionsUrl, image, carClass, availableOnRequest, additionalCosts } = option
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const { isTablet } = useBreakpoints()
  const featuresAlignment = isTablet ? 'inlineLeft' : 'list'

  const sendClickConditionsEvent = () => {
    sendEvent(
      createCarEvent({
        result: 'conditions page opened',
        type: id,
        description: 'Car conditions link clicked',
        element: 'button',
      }),
    )
  }

  const handleConditionsLinkClick = () => {
    sendClickConditionsEvent()
  }

  const handleConditionsButtonClick = () => {
    window.open(conditionsUrl, '_blank')
    sendClickConditionsEvent()
  }

  return (
    <>
      {image && (
        <MediaServerLazyImage
          src={image ?? ''}
          widths={[160]}
          alt={label}
          width={300}
          height={300}
          loading="lazy"
          className={cn(styles.carImage)}
        />
      )}
      <div className={styles.carHeader}>
        <Typography variant="display-h-regular" className={styles.carName}>
          {label}
        </Typography>
        <Typography variant="body-s-regular-tight">{carClass}</Typography>
        <Benefits.Root display="inlineLeft" className={cn(styles.carFeatures)}>
          {features.map(({ glyph, text }) => (
            <Benefits.Item key={glyph} title={text ?? ''} icon={glyph} className={cn(styles.carFeatureItem)} />
          ))}
        </Benefits.Root>
      </div>

      <div className={styles.carInformationDetails}>
        <Benefits.Root display={featuresAlignment} className={cn(styles.carFeatures)}>
          {extraFeatures.map(({ glyph, values }, index) => {
            const title
              = values
                ?.map((text) => {
                  const featureTextKey = featureTextKeys(toSnakeCase(text) as FeatureType) || ''

                  return featureTextKey ? intl.formatMessage({ id: featureTextKey }) : text
                })
                .join(', ') ?? ''
            return (
              <Benefits.Item
                key={`${glyph ?? 'feature'}-${title}-${index}`}
                title={title}
                icon={glyph}
                className={cn(styles.carFeatureItem)}
              />
            )
          })}
          {additionalCosts?.oneWay?.aproxFeeAmount !== undefined && additionalCosts.oneWay.aproxFeeAmount > 0 && (
            <Benefits.Item
              title={intl.formatMessage(
                { id: `checkout_steps_ancillaries_car_rental_filtering_oneway_fee_text` },
                { price: intl.formatCurrency(additionalCosts.oneWay.aproxFeeAmount) },
              )}
              icon="OneWaySign"
            />
          )}
        </Benefits.Root>
        <div className={styles.legalMenuBar}>
          <a
            onClick={handleConditionsLinkClick}
            href={conditionsUrl}
            target="_blank"
            rel="noopener noreferrer"
            title={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_car_conditions_text' })}
            className={styles.link}
          >
            <Typography>
              {intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_car_conditions_text' })}
            </Typography>
          </a>
          {availableOnRequest && (
            <>
              <Typography className={styles.legalMenuBarSeparator} variant="body-s-regular-tight">
                &nbsp;·&nbsp;
              </Typography>
              <Sheet.Root>
                <Sheet.Trigger>
                  <Typography className={styles.onRequest}>
                    {intl.formatMessage({
                      id: 'checkout_steps_ancillaries_car_rental_filtering_available_on_request_label',
                    })}
                  </Typography>
                </Sheet.Trigger>
                <Sheet.Body side="right" hasPadding>
                  <Sheet.Header
                    closeLabel={intl.formatMessage({ id: 'close' })}
                    titleLabel={intl.formatMessage({
                      id: 'checkout_steps_ancillaries_car_rental_filtering_available_on_request_title',
                    })}
                  />
                  <Sheet.Content>
                    <RichText
                      innerHTML={intl.formatMessage(
                        { id: 'checkout_steps_ancillaries_car_rental_filtering_available_on_request_content' },
                        {},
                        { ignoreTag: true },
                      )}
                    />
                  </Sheet.Content>
                </Sheet.Body>
              </Sheet.Root>
            </>
          )}
        </div>

        <AdditionalCostBanners
          additionalCosts={additionalCosts}
          onConditionsButtonClick={handleConditionsButtonClick}
        />
      </div>
    </>
  )
}
